/**
 * The item caches, the ordered recent-stash ID pages and the small lists
 * painted first (the tag rail, upcoming events) persist across sessions, so
 * a reopen paints from disk while the network catches up.
 *
 * What persists is bounded. A heavy user's cache once reached 115 MB of
 * JSON (12,000 units): hydrated before first render at every boot and
 * rewritten wholesale on every cache event, it was the load that killed the
 * content process, invisible to any image or map count. Now the recent-ID
 * page and the most recently used few hundred units per family are kept;
 * the rest is refetched when asked for.
 *
 * The cache belongs to a signed-in session. Boot restores it only when a
 * session exists, so welcome, login, and signup never touch the store;
 * logout removes it. Writes happen at most once a minute while the app is
 * in use, from the query-cache subscription only. There is deliberately no
 * write on backgrounding or unload: WebKit never completes an IndexedDB
 * transaction started by a document that is being torn down, and every
 * later transaction on that object store queues behind it for the life of
 * the process. That blanked the iOS login screen (SN-816): the welcome page
 * wrote the cache on `visibilitychange: hidden` during its own navigation,
 * and the next document's restore read never returned. Losing up to a
 * minute of cache to a background kill costs a refetch.
 *
 * The cache write model is upsert: responses ADD new units and UPDATE the
 * fields they carry; an omitted field does not erase an existing field.
 * When a persisted shape changes, bump CACHE_SCHEMA_VERSION and the old
 * blob is dropped on the next boot.
 */
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import type { InfiniteData, Query, QueryClient } from "@tanstack/react-query";
import type { PersistedClient, Persister } from "@tanstack/react-query-persist-client";
import { del, get, set } from "idb-keyval";

export const CACHE_SCHEMA_VERSION = "v3"; // v3: bounded persisted set.
export const CACHE_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days on disk
/** How often the cache is written to disk while in use. */
export const PERSIST_INTERVAL_MS = 60 * 1000;
/** Most recently used units kept on disk, per family. */
export const PERSISTED_UNITS_PER_FAMILY: Record<string, number> = {
  entity: 300,
  stash: 300,
  "entity-post-matches": 600,
};

const STORAGE_KEY = "stasht-unit-cache";

/**
 * Only item units, the recent-stash ID list and the first-paint lists
 * survive restart — matched by exact key shape, never by prefix.
 * Satellite queries sharing a prefix (["entity", id, "connected"], stash
 * detail keys) are session-only; their data need not be JSON-safe.
 * The lists are bounded by their request limit (events) or by the user's
 * own tag count; filtered tag stats stay session-only.
 */
const isRecentIdsKey = (key: readonly unknown[]): boolean =>
  key[0] === "stashes" && key[1] === "recent" && key[2] === "ids" && key.length === 4;

const isPersistedCacheKey = (key: readonly unknown[]): boolean =>
  (key[0] === "entity" && key.length === 2) || // entity units
  (key[0] === "stash" && key.length === 2) || // post units
  (key[0] === "entity-post-matches" && key.length === 3) || // map strip match ids
  isRecentIdsKey(key) ||
  (key[0] === "user-tags" && key.length === 1) || // tag rail
  (key[0] === "tag-stats" && key[1] === null && key.length === 2) || // tag rail counts, unfiltered
  (key[0] === "events" && key[1] === "upcoming" && key.length === 5); // Events page

export const shouldPersistQuery = (query: Query): boolean =>
  query.state.status === "success" && isPersistedCacheKey(query.queryKey);

const dehydrateOptions = { shouldDehydrateQuery: shouldPersistQuery };

type PersistedQuery = PersistedClient["clientState"]["queries"][number];

/** A cold open reads every restored page again, so only the first goes to disk. */
const firstPageOnly = (query: PersistedQuery): PersistedQuery => {
  const data = query.state.data as InfiniteData<unknown> | undefined;
  if (!data?.pages) return query;
  return {
    ...query,
    state: { ...query.state, data: { pages: data.pages.slice(0, 1), pageParams: data.pageParams.slice(0, 1) } },
  };
};

/**
 * Keep the recent list's first page and, per unit family, the most recently
 * updated units up to the family's cap. Pure; exported for its test.
 */
export const boundPersistedClient = (
  client: PersistedClient,
  caps: Record<string, number> = PERSISTED_UNITS_PER_FAMILY,
): PersistedClient => {
  const kept: PersistedQuery[] = [];
  const byFamily = new Map<string, PersistedQuery[]>();
  for (const query of client.clientState.queries) {
    const family = String(query.queryKey[0]);
    if (!(family in caps)) {
      kept.push(isRecentIdsKey(query.queryKey) ? firstPageOnly(query) : query);
      continue;
    }
    const bucket = byFamily.get(family) ?? [];
    bucket.push(query);
    byFamily.set(family, bucket);
  }
  for (const [family, bucket] of byFamily) {
    bucket.sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt);
    kept.push(...bucket.slice(0, caps[family]));
  }
  return { ...client, clientState: { ...client.clientState, queries: kept } };
};

const storage = {
  getItem: (key: string) => get<string>(key).then(value => value ?? null),
  setItem: (key: string, value: string) => set(key, value),
  removeItem: (key: string) => del(key),
};

const throttled = createAsyncStoragePersister({ key: STORAGE_KEY, throttleTime: PERSIST_INTERVAL_MS, storage });

export const cachePersister: Persister = {
  persistClient: client => throttled.persistClient(boundPersistedClient(client)),
  restoreClient: throttled.restoreClient,
  removeClient: throttled.removeClient,
};

export const persistence = (queryClient: QueryClient) => ({
  queryClient,
  persister: cachePersister,
  maxAge: CACHE_MAX_AGE_MS,
  buster: CACHE_SCHEMA_VERSION,
  dehydrateOptions,
});

/**
 * The unit write rule: upsert — add when new, update the fields the response
 * carries, never lose fields the response doesn't mention.
 */
export function upsert<T extends object>(prev: T | null | undefined, next: T): T {
  return prev ? { ...prev, ...next } : next;
}
